/**
 * V70G7B — 팝업 공지 조회/기록. V70G7D — 변경 신호 구독으로 장시간 접속 사용자에게도 반영.
 * V70G8B — 팝업 소스 2종 (일반 공지 'erp', 게임대회 대시보드 공지 'gameCenter').
 *   소스마다 Callable·신호 문서·닫기 기록·세션 키가 분리되어 서로 섞이지 않는다.
 * 조회·기록 실패는 ERP 사용을 막지 않도록 모두 무시(console.warn)한다.
 */
import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../firebase/config';
import type { ActivePopupAnnouncement, PopupSource } from './useAnnouncements';
import { PopupFeedResult, PopupSyncController } from './popupSync';
import { popupDetailKey } from './popupHold';

export type PopupRecordAction = 'shown' | 'dismiss' | 'hideForDay';

export interface PopupSourceConfig {
  source: PopupSource;
  listCallable: string;
  dismissCallable: string;
  signalDocId: string;
  sessionPrefix: string;
  label: string;
  detailPath: (announcementId: string) => string;
}

const SESSION_PREFIX = 'popup.session.';

export const POPUP_SOURCES: Record<PopupSource, PopupSourceConfig> = {
  erp: {
    source: 'erp',
    listCallable: 'listActivePopupAnnouncements',
    dismissCallable: 'dismissPopupAnnouncement',
    signalDocId: 'announcementPopups',
    sessionPrefix: SESSION_PREFIX,
    label: '공지',
    detailPath: (id) => `/announcements/${id}`,
  },
  gameCenter: {
    source: 'gameCenter',
    listCallable: 'listActiveGameCenterPopups',
    dismissCallable: 'dismissGameCenterPopup',
    signalDocId: 'gameCenterPopups',
    sessionPrefix: `${SESSION_PREFIX}gc.`,
    label: '게임대회 공지',
    detailPath: (id) => `/tournament/dashboard?announcement=${encodeURIComponent(id)}`,
  },
};

function sourceOf(popup: Pick<ActivePopupAnnouncement, 'source'>): PopupSourceConfig {
  return POPUP_SOURCES[popup.source ?? 'erp'];
}

export function sessionHiddenKey(
  uid: string,
  announcementId: string,
  revision: number,
  source: PopupSource = 'erp',
): string {
  return `${POPUP_SOURCES[source].sessionPrefix}${uid}.${announcementId}.${revision}`;
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

type SessionPopupRef = Pick<ActivePopupAnnouncement, 'id' | 'popupRevision' | 'source'>;

export function isSessionHidden(uid: string, popup: SessionPopupRef): boolean {
  try {
    const key = sessionHiddenKey(uid, popup.id, popup.popupRevision, popup.source ?? 'erp');
    return safeSessionStorage()?.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function markSessionHidden(uid: string, popup: SessionPopupRef): void {
  try {
    const key = sessionHiddenKey(uid, popup.id, popup.popupRevision, popup.source ?? 'erp');
    safeSessionStorage()?.setItem(key, '1');
  } catch {
    /* storage 차단 시 이번 화면에서만 숨김 */
  }
}

/** 로그아웃 시 세션 숨김 기록 전체 삭제 (모든 소스: 'popup.session.' 접두) */
export function clearPopupSessionState(): void {
  const ss = safeSessionStorage();
  if (!ss) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < ss.length; i++) {
      const k = ss.key(i);
      if (k && k.startsWith(SESSION_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => ss.removeItem(k));
  } catch {
    /* ignore */
  }
}

/** 서버 결과에서 현재 세션에서 숨긴 'session' 정책 팝업 제외 */
export function filterSessionHidden(uid: string, popups: ActivePopupAnnouncement[]): ActivePopupAnnouncement[] {
  return popups.filter((p) => !(p.popupDismissPolicy === 'session' && isSessionHidden(uid, p)));
}

function newClientRequestId(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export async function recordPopupAction(
  popup: Pick<ActivePopupAnnouncement, 'id' | 'source'>,
  action: PopupRecordAction,
): Promise<void> {
  if (!functions) return;
  try {
    const fn = httpsCallable<
      { announcementId: string; action: PopupRecordAction; clientRequestId: string },
      { success: boolean; reused?: boolean }
    >(functions, sourceOf(popup).dismissCallable);
    await fn({ announcementId: popup.id, action, clientRequestId: newClientRequestId() });
  } catch (e) {
    console.warn('[popup] 기록 실패 (무시)', action, e);
  }
}

export function popupDetailPath(popup: Pick<ActivePopupAnnouncement, 'id' | 'source'>): string {
  return sourceOf(popup).detailPath(popup.id);
}

export function popupSourceLabel(popup: Pick<ActivePopupAnnouncement, 'source'>): string {
  return sourceOf(popup).label;
}

/** V70G8E: 팝업 → 상세 키 (상세 화면의 useHoldPopups(…, key) 와 같은 규칙) */
export function popupDetailKeyOf(popup: Pick<ActivePopupAnnouncement, 'id' | 'source'>): string {
  return popupDetailKey(popup.source ?? 'erp', popup.id);
}

/**
 * V70G8E: 현재 주소가 공지 상세 진입 주소면 상세 키, 아니면 null.
 *   - 일반 공지 상세: /announcements/:id  (new·edit 제외)
 *   - 게임대회 공지 상세: /tournament/dashboard?announcement=:id
 */
export function detailKeyFromLocation(pathname: string, search: string | undefined): string | null {
  if (pathname === '/tournament/dashboard') {
    const id = new URLSearchParams(search || '').get('announcement');
    return id ? popupDetailKey('gameCenter', id) : null;
  }
  const m = /^\/announcements\/([^/]+)\/?$/.exec(pathname);
  if (m && m[1] !== 'new') {
    try {
      return popupDetailKey('erp', decodeURIComponent(m[1]));
    } catch {
      return popupDetailKey('erp', m[1]);
    }
  }
  return null;
}

/** 상세 경로에서 화면 경로(쿼리 제외)만 */
export function pathnameOf(path: string): string {
  const q = path.indexOf('?');
  return q >= 0 ? path.slice(0, q) : path;
}

export const POPUP_SIGNAL_PATH = ['systemSignals', POPUP_SOURCES.erp.signalDocId] as const;

/** 서버 응답 항목에 소스 표시 (키·기록·세션 분리용) */
export function tagSource(popups: ActivePopupAnnouncement[], source: PopupSource): ActivePopupAnnouncement[] {
  return popups.map((p) => ({ ...p, source }));
}

async function fetchActivePopups(cfg: PopupSourceConfig): Promise<PopupFeedResult> {
  if (!functions) return { popups: [], nextCheckAt: null };
  const fn = httpsCallable<
    Record<string, never>,
    { success: boolean; popups: ActivePopupAnnouncement[]; nextCheckAt?: string | null }
  >(functions, cfg.listCallable);
  const res = await fn({});
  const list = Array.isArray(res.data?.popups) ? res.data.popups : [];
  return {
    popups: tagSource(list, cfg.source),
    nextCheckAt: res.data?.nextCheckAt ?? null,
  };
}

/**
 * 소스 하나의 활성 팝업 목록을 유지한다.
 * 최초 조회 + 변경 신호(onSnapshot) + 탭 복귀/포커스/온라인 + 예정 시각/KST 자정 재조회.
 * uid 가 바뀌거나, enabled=false 가 되거나, 언마운트되면 구독·타이머를 정리하고 이전 응답은 버린다.
 */
export function usePopupFeed(uid: string | null, source: PopupSource, enabled = true): ActivePopupAnnouncement[] {
  const [popups, setPopups] = useState<ActivePopupAnnouncement[]>([]);

  useEffect(() => {
    setPopups([]);
    if (!uid || !functions || !enabled) return undefined;
    const cfg = POPUP_SOURCES[source];

    const controller = new PopupSyncController({
      fetchPopups: () => fetchActivePopups(cfg),
      subscribe: (onVersion, onError) => {
        if (!db) {
          onError(new Error('firestore unavailable'));
          return () => undefined;
        }
        return onSnapshot(
          doc(db, 'systemSignals', cfg.signalDocId),
          (snap) => {
            const v = snap.get('version');
            onVersion(typeof v === 'number' ? v : 0);
          },
          onError,
        );
      },
      onUpdate: (result) => setPopups(filterSessionHidden(uid, result.popups)),
      now: () => Date.now(),
      random: () => Math.random(),
      setTimer: (fn, ms) => window.setTimeout(fn, ms),
      clearTimer: (h) => window.clearTimeout(h as number),
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') controller.resume();
    };
    const onResume = () => controller.resume();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onResume);
    window.addEventListener('online', onResume);
    controller.start();

    return () => {
      controller.stop();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('online', onResume);
    };
  }, [uid, source, enabled]);

  return popups;
}

/** 일반 공지 팝업 (V70G7D 와 동일 동작) */
export function useActivePopupAnnouncements(uid: string | null): ActivePopupAnnouncement[] {
  return usePopupFeed(uid, 'erp', true);
}
